import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '@app/core/http/api.service';
import {
    IOcbApiAzureStorageCredentialsParameter,
    IOcbApiSshKeyParameter,
    IOcbApiStringParameter,
    IOcbAzureStorageRemotePlatform,
    IOcbSaveFtpRemotePlatform,
    IOcbSaveHttpRemotePlatform,
    IOcbSaveSftpRemotePlatform,
    IOcbSaveSharePointRemotePlatform,
    IOcbFTPRemotePlatform,
    IOcbHTTPRemotePlatform,
    IOcbLocalAgentCommand,
    IOcbRemotePlatform,
    IOcbRemotePlatformLog,
    IOcbSaveAzureStorageRemotePlatform,
    IOcbSFTPRemotePlatform,
    IOcbSharePointRemotePlatform,
    OcbRemotePlatformLogTypes,
} from '@app/core/models';
import { environment as env } from '@env/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class RemotePlatformsService extends ApiService {

    private remotePlatformsUrl: string;
    private azureStorageRemotePlatformsUrl: string;
    private ftpRemotePlatformsUrl: string;
    private httpRemotePlatformsUrl: string;
    private sftpRemotePlatformsUrl: string;
    private spRemotePlatformsUrl: string;

    private options = {
        headers: this.defaultHeaders,
        observe: 'body' as 'body',
    };

    constructor(httpClient: HttpClient) {
        super(httpClient);

        if (!env.api.endpoint) {
            throw new Error('Could not initialize the "remote-platforms" service, the proxyPath configuration is invalid.');
        }

        this.remotePlatformsUrl = `${env.api.endpoint}/RemotePlatforms`;
        this.azureStorageRemotePlatformsUrl = `${this.remotePlatformsUrl}/AzureStorage`;
        this.ftpRemotePlatformsUrl = `${this.remotePlatformsUrl}/FTP`;
        this.httpRemotePlatformsUrl = `${this.remotePlatformsUrl}/HTTP`;
        this.sftpRemotePlatformsUrl = `${this.remotePlatformsUrl}/SFTP`;
        this.spRemotePlatformsUrl = `${this.remotePlatformsUrl}/SharePoint`;
    }

    /**
     * Get the list of general information of remote platforms
     * @param environmentId Internal identifier of the environment
     * @param type Type of the remote platforms to return
     * @param includeDisabled set true to get disabled remote platforms
     */
    public getRemotePlatforms(environmentId?: string, type?: string, includeDisabled: boolean = true): Observable<IOcbRemotePlatform[]> {

        let url = `${this.remotePlatformsUrl}?IncludeDisabled=${includeDisabled}`;
        if (environmentId) { url += `&EnvironmentId=${environmentId}`; }
        if (type) { url += `&Type=${type}`; }

        return this.httpClient.get<IOcbRemotePlatform[]>(url, this.options);
    }

    /**
     * Get general information about a remote platform
     * @param remotePlatformId Internal identifier of the remote platform
     */
    public getRemotePlatform(remotePlatformId: string): Observable<IOcbRemotePlatform> {
        const url = `${this.remotePlatformsUrl}/${remotePlatformId}`;
        return this.httpClient.get<IOcbRemotePlatform>(url, this.options);
    }

    // Azure Storage

    /**
     * Get the list of Azure Storage remote platforms
     * @param environmentId Internal identifier of the environment
     * @param includeDisabled set true to get disabled remote platforms
     */
    public getAzureStorageRemotePlatforms(environmentId?: string, includeDisabled: boolean = true): Observable<IOcbAzureStorageRemotePlatform[]> {

        let url = `${this.azureStorageRemotePlatformsUrl}?IncludeDisabled=${includeDisabled}`;
        if (environmentId) { url += `&EnvironmentId=${environmentId}`; }

        return this.httpClient.get<IOcbAzureStorageRemotePlatform[]>(url, this.options);
    }

    /**
    * Add an Azure Storage remote platform
    * @param remotePlatform Remote platform to create
     */
    public addAzureStorageRemotePlatform(remotePlatform: IOcbSaveAzureStorageRemotePlatform): Observable<IOcbAzureStorageRemotePlatform> {
        return this.httpClient.post<IOcbAzureStorageRemotePlatform>(this.azureStorageRemotePlatformsUrl, remotePlatform, this.options);
    }

    /**
     * Get an Azure Storage remote platform
     * @param remotePlatformId Internal identifier of the remote platform
     */
    public getAzureStorageRemotePlatform(remotePlatformId: string): Observable<IOcbAzureStorageRemotePlatform> {
        const url = `${this.azureStorageRemotePlatformsUrl}/${remotePlatformId}`;
        return this.httpClient.get<IOcbAzureStorageRemotePlatform>(url, this.options);
    }

    /**
     * Update an Azure Storage remote platform
     * @param remotePlatformId Internal identifier of the remote platform
     * @param remotePlatform Data to update
     */
    public updateAzureStorageRemotePlatform(remotePlatformId: string, remotePlatform: IOcbSaveAzureStorageRemotePlatform): Observable<void> {
        const url = `${this.azureStorageRemotePlatformsUrl}/${remotePlatformId}`;
        return this.httpClient.put<void>(url, remotePlatform, this.options);
    }

    /**
     * Delete an Azure Storage remote platform
     * @param remotePlatformId Internal identifier of the remote platform
     */
    public deleteAzureStorageRemotePlatform(remotePlatformId: string): Observable<void> {
        const url = `${this.azureStorageRemotePlatformsUrl}/${remotePlatformId}`;
        return this.httpClient.delete<void>(url, this.options);
    }

    // FTP

    /**
     * Get the list of FTP remote platforms
     * @param environmentId Internal identifier of the environment
     * @param includeDisabled set true to get disabled remote platforms
     */
    public getFTPRemotePlatforms(environmentId?: string, includeDisabled: boolean = true): Observable<IOcbFTPRemotePlatform[]> {

        let url = `${this.ftpRemotePlatformsUrl}?IncludeDisabled=${includeDisabled}`;
        if (environmentId) { url += `&EnvironmentId=${environmentId}`; }

        return this.httpClient.get<IOcbFTPRemotePlatform[]>(url, this.options);
    }

    /**
     * Add a FTP remote platform
     * @param remotePlatform Remote platform to create
     */
    public addFTPRemotePlatform(remotePlatform: IOcbSaveFtpRemotePlatform): Observable<IOcbFTPRemotePlatform> {
        return this.httpClient.post<IOcbFTPRemotePlatform>(this.ftpRemotePlatformsUrl, remotePlatform, this.options);
    }

    /**
     * Get a FTP remote platform
     * @param remotePlatformId Internal identifier of the remote platform
     */
    public getFTPRemotePlatform(remotePlatformId: string): Observable<IOcbFTPRemotePlatform> {
        const url = `${this.ftpRemotePlatformsUrl}/${remotePlatformId}`;
        return this.httpClient.get<IOcbFTPRemotePlatform>(url, this.options);
    }

    /**
     * Update a FTP remote platform
     * @param remotePlatformId Internal identifier of the remote platform
     * @param remotePlatform Data to update
     */
    public updateFTPRemotePlatform(remotePlatformId: string, remotePlatform: IOcbSaveFtpRemotePlatform): Observable<void> {
        const url = `${this.ftpRemotePlatformsUrl}/${remotePlatformId}`;
        return this.httpClient.put<void>(url, remotePlatform, this.options);
    }

    /**
     * Delete a FTP remote platform
     * @param remotePlatformId Internal identifier of the remote platform
     */
    public deleteFTPRemotePlatform(remotePlatformId: string): Observable<void> {
        const url = `${this.ftpRemotePlatformsUrl}/${remotePlatformId}`;
        return this.httpClient.delete<void>(url, this.options);
    }

    // HTTP

    /**
     * Get the list of HTTP remote platforms
     * @param environmentId Internal identifier of the environment
     * @param includeDisabled set true to get disabled remote platforms
     */
    public getHTTPRemotePlatforms(environmentId?: string, includeDisabled: boolean = true): Observable<IOcbHTTPRemotePlatform[]> {

        let url = `${this.httpRemotePlatformsUrl}?IncludeDisabled=${includeDisabled}`;
        if (environmentId) { url += `&EnvironmentId=${environmentId}`; }

        return this.httpClient.get<IOcbHTTPRemotePlatform[]>(url, this.options);
    }

    /**
     * Add a HTTP remote platform
     * @param remotePlatform Remote platform to create
     */
    public addHTTPRemotePlatform(remotePlatform: IOcbSaveHttpRemotePlatform): Observable<IOcbHTTPRemotePlatform> {
        return this.httpClient.post<IOcbHTTPRemotePlatform>(this.httpRemotePlatformsUrl, remotePlatform, this.options);
    }

    /**
     * Get a HTTP remote platform
     * @param remotePlatformId Internal identifier of the remote platform
     */
    public getHTTPRemotePlatform(remotePlatformId: string): Observable<IOcbHTTPRemotePlatform> {
        const url = `${this.httpRemotePlatformsUrl}/${remotePlatformId}`;
        return this.httpClient.get<IOcbHTTPRemotePlatform>(url, this.options);
    }

    /**
     * Update a HTTP remote platform
     * @param remotePlatformId Internal identifier of the remote platform
     * @param remotePlatform Data to update
     */
    public updateHTTPRemotePlatform(remotePlatformId: string, remotePlatform: IOcbSaveHttpRemotePlatform): Observable<void> {
        const url = `${this.httpRemotePlatformsUrl}/${remotePlatformId}`;
        return this.httpClient.put<void>(url, remotePlatform, this.options);
    }

    /**
     * Delete a HTTP remote platform
     * @param remotePlatformId Internal identifier of the remote platform
     */
    public deleteHTTPRemotePlatform(remotePlatformId: string): Observable<void> {
        const url = `${this.httpRemotePlatformsUrl}/${remotePlatformId}`;
        return this.httpClient.delete<void>(url, this.options);
    }

    // SFTP

    /**
     * Get the list of SFTP remote platforms
     * @param environmentId Internal identifier of the environment
     * @param includeDisabled set true to get disabled remote platforms
     */
    public getSFTPRemotePlatforms(environmentId?: string, includeDisabled: boolean = true): Observable<IOcbSFTPRemotePlatform[]> {

        let url = `${this.sftpRemotePlatformsUrl}?IncludeDisabled=${includeDisabled}`;
        if (environmentId) { url += `&EnvironmentId=${environmentId}`; }

        return this.httpClient.get<IOcbSFTPRemotePlatform[]>(url, this.options);
    }

    /**
     * Add a SFTP remote platform
     * @param remotePlatform Remote platform to create
     */
    public addSFTPRemotePlatform(remotePlatform: IOcbSaveSftpRemotePlatform): Observable<IOcbSFTPRemotePlatform> {
        return this.httpClient.post<IOcbSFTPRemotePlatform>(this.sftpRemotePlatformsUrl, remotePlatform, this.options);
    }

    /**
     * Get a SFTP remote platform
     * @param remotePlatformId Internal identifier of the remote platform
     */
    public getSFTPRemotePlatform(remotePlatformId: string): Observable<IOcbSFTPRemotePlatform> {
        const url = `${this.sftpRemotePlatformsUrl}/${remotePlatformId}`;
        return this.httpClient.get<IOcbSFTPRemotePlatform>(url, this.options);
    }

    /**
     * Update a SFTP remote platform
     * @param remotePlatformId Internal identifier of the remote platform
     * @param remotePlatform Data to update
     */
    public updateSFTPRemotePlatform(remotePlatformId: string, remotePlatform: IOcbSaveSftpRemotePlatform): Observable<void> {
        const url = `${this.sftpRemotePlatformsUrl}/${remotePlatformId}`;
        return this.httpClient.put<void>(url, remotePlatform, this.options);
    }

    /**
     * Delete a SFTP remote platform
     * @param remotePlatformId Internal identifier of the remote platform
     */
    public deleteSFTPRemotePlatform(remotePlatformId: string): Observable<void> {
        const url = `${this.sftpRemotePlatformsUrl}/${remotePlatformId}`;
        return this.httpClient.delete<void>(url, this.options);
    }

    // SharePoint

    /**
     * Get the list of SharePoint remote platforms
     * @param environmentId Internal identifier of the environment
     */
    public getSharePointRemotePlatforms(environmentId?: string, includeDisabled: boolean = true): Observable<IOcbSharePointRemotePlatform[]> {

        let url = `${this.spRemotePlatformsUrl}?IncludeDisabled=${includeDisabled}`;
        if (environmentId) { url += `&EnvironmentId=${environmentId}`; }

        return this.httpClient.get<IOcbSharePointRemotePlatform[]>(url, this.options);
    }

    /**
     * Add a SharePoint remote platform
     * @param remotePlatform Remote platform to create
     */
    public addSharePointRemotePlatform(remotePlatform: IOcbSaveSharePointRemotePlatform): Observable<IOcbSharePointRemotePlatform> {
        return this.httpClient.post<IOcbSharePointRemotePlatform>(this.spRemotePlatformsUrl, remotePlatform, this.options);
    }

    /**
     * Get a SharePoint remote platform
     * @param remotePlatformId Internal identifier of the remote platform
     */
    public getSharePointRemotePlatform(remotePlatformId: string): Observable<IOcbSharePointRemotePlatform> {
        const url = `${this.spRemotePlatformsUrl}/${remotePlatformId}`;
        return this.httpClient.get<IOcbSharePointRemotePlatform>(url, this.options);
    }

    /**
     * Update a SharePoint remote platform
     * @param remotePlatformId Internal identifier of the remote platform
     * @param remotePlatform Data to update
     */
    public updateSharePointRemotePlatform(remotePlatformId: string, remotePlatform: IOcbSaveSharePointRemotePlatform): Observable<void> {
        const url = `${this.spRemotePlatformsUrl}/${remotePlatformId}`;
        return this.httpClient.put<void>(url, remotePlatform, this.options);
    }

    /**
     * Delete a SharePoint remote platform
     * @param remotePlatformId Internal identifier of the remote platform
     */
    public deleteSharePointRemotePlatform(remotePlatformId: string): Observable<void> {
        const url = `${this.spRemotePlatformsUrl}/${remotePlatformId}`;
        return this.httpClient.delete<void>(url, this.options);
    }

    // LOGS

    /**
     * Get a remote platform log
     * @param logId Internal identifier of the log
     */
    public getLog(logId: string): Observable<IOcbRemotePlatformLog> {

        let url = `${this.remotePlatformsUrl}/Logs/${logId}`;
        return this.httpClient.get<IOcbRemotePlatformLog>(url, this.options).pipe(map(log => this.mapLogTypesToEnums(log)));
    }

    /**
     * Get the list of the logs of a remote platform
     * @param remotePlatformId Internal identifier of the remote platform
     * @param utcDateFrom UTC Start date (time will be truncated)
     * @param utcDateTo UTC End date (time will be truncated)
     */
    public getLogs(remotePlatformId: string, utcDateFrom?: string, utcDateTo?: string): Observable<IOcbRemotePlatformLog[]> {

        let url = `${this.remotePlatformsUrl}/${remotePlatformId}/Logs?`;

        if (utcDateFrom) { url = `${url}UtcDateFrom=${utcDateFrom}&`; }
        if (utcDateTo) { url = `${url}UtcDateTo=${utcDateTo}`; }

        return this.httpClient.get<IOcbRemotePlatformLog[]>(url, this.options).pipe(
            map(logs => logs.map(log => this.mapLogTypesToEnums(log))),
        );
    }

    /**
     * Get the list of the logs of the remote platforms
     * @param utcDateFrom UTC Start date (time will be truncated)
     * @param utcDateTo UTC End date (time will be truncated)
     */
    public getAllLogs(utcDateFrom: string, utcDateTo: string, environmentId?: string): Observable<IOcbRemotePlatformLog[]> {

        let url = `${this.remotePlatformsUrl}/Logs?`;

        if (utcDateFrom) { url = `${url}UtcDateFrom=${utcDateFrom}`; }
        if (utcDateTo) {
            if (!url.endsWith("?")) { url = url + "&"; }
            url = `${url}UtcDateTo=${utcDateTo}`;
        }
        if (environmentId) {
            if (!url.endsWith("?")) { url = url + "&"; }
            url = `${url}EnvironmentId=${environmentId}`;
        }

        return this.httpClient.get<IOcbRemotePlatformLog[]>(url, this.options).pipe(
            map(logs => logs.map(log => this.mapLogTypesToEnums(log))),
        );
    }

    // CHECK CONNECTIONS

    /**
     * Request the checking of the connection to a remote platform
     * @param remotePlatformId Internal identifier of the remote platform
     * @param agentId Internal identifier of the agent
     * @param remoteFolderPath Path to the folder on the remote platform
     * @param waitForExecution set true to wait for the end of the execution of the check
     * @param sftpEngine set the engine to use for testing connection to a SFTP platform
     */
    public checkConnection(remotePlatformId: string, agentId: string, remoteFolderPath: string, waitForExecution: boolean = true, sftpEngine?: string): Observable<IOcbLocalAgentCommand> {
        const url = !sftpEngine
            ? `${this.remotePlatformsUrl}/${remotePlatformId}/Commands/CheckConnection/${agentId}?WaitForExecution=${waitForExecution}`
            : `${this.remotePlatformsUrl}/${remotePlatformId}/Commands/CheckConnection/${agentId}?WaitForExecution=${waitForExecution}&sftpEngine=${sftpEngine}`;
        const body = { value: remoteFolderPath };
        return this.httpClient.post<IOcbLocalAgentCommand>(url, body, this.options);
    }

    /**
     * Get list of directories used in file transfers for a remote platform
     * @param remotePlatformId Internal identifier of the remote platform
     */
    public getDirectories(remotePlatformId: string): Observable<string[]> {

        let url = `${this.remotePlatformsUrl}/${remotePlatformId}/directories`;
        return this.httpClient.get<string[]>(url, this.options);
    }

    // MAPPINGS

    /**
     * Map logs enum numbers to enum arrays.
     */
    private mapLogTypesToEnums(log: IOcbRemotePlatformLog): IOcbRemotePlatformLog {
        log.logTypeEnums = this.splitEnums(log.logType, OcbRemotePlatformLogTypes);
        return log;
    }
}
